// Constants for AI prompts and messages
export const GENEALOGIST_SYSTEM_MESSAGE =
  'You are a professional genealogist specializing in analyzing historical documents and extracting genealogical information.'

export const GENEALOGIST_SYSTEM_MESSAGE_OBJECT = {
  role: 'system',
  content: GENEALOGIST_SYSTEM_MESSAGE,
}

export const RELATIONSHIP_ANALYZER_SYSTEM_MESSAGE =
  'You are a professional genealogist specializing in analyzing historical documents and connecting genealogical relationships.'

export const createPersonExtractionPrompt = (transcript) => {
  const introduction =
    'I am going to upload a document which text was extracted via an automated handwriting recognition system. ' +
    'Particularly, I am interested in the people names, even names (ultimately connected with people names), ' +
    'dates (ultimately connected with events), occupations (ultimately connected with people names) and ' +
    'relationships among those people.'

  const hwrChallenges =
    "Given that it's HWR extracted text, there are several errors. The same person's name might be " +
    "represented several different ways. The actual correct spelling of the person's name may not even be " +
    'in the text. Also, the text we have is unfortunately, not from beginning of the page to the end. ' +
    'The text is based on what was on that particular image and often times, the entire document spans ' +
    'multiple images.'

  const relationshipGuidance =
    "Don't assume there is a relationship. Often times, there are just legal parties involved in a will " +
    "and probate who don't have family relationships to others mentioned in the document. Also, use hints " +
    'of a surnames of parents and children to possibly correct either surname if they are close enough and ' +
    'depending on the likelihood of the name. For example, there are several variations of names in there ' +
    "which I've heard of being a surname or even given name of a person."

  const instructions =
    'For each person you find, generate a name object that splits the name into first name and last name ' +
    '(surname), an id (which is a number that starts at 0 and adds 1 each time it is used so that each ' +
    'person has a unique id), primary, which is true if this is the principle person involved in the event, ' +
    'put all related persons (their names) to the current person in the relationships array including their ' +
    'familial type (e.g. son, daughter, husband).'

  const outputFormat = `Output the data in this format: {
  "name": {
    "fullName": [FirstName1] [LastName1],
    "first": [FirstName1],
    "last": [LastName1],
  }
  "id": [UniqueId1],
  "events": [{date, place, type}]
  "principle": [true/false]
  "relationships": [{id, name, relationshipType}]
}`

  return `
  ${introduction}\n
  ${hwrChallenges}\n
  ${relationshipGuidance}
  \n${instructions}
  \n${outputFormat}
  \nThe document is as follows:\n
  '''
  ${transcript}
  '''`
}

export const createRelationshipLinkingPrompt = (transcript, parsedData) => {
  const introduction =
    'I am going to upload a document which text was extracted via an automated handwriting recognition system. ' +
    'I will also send a list of people with information about their role in the event, which is a json object ' +
    'generated by an AI. I want you to look through the json object, specifically at the relationship properties, ' +
    'and add a property called relationshipLink that will contain more information about the relationship. ' +
    'Then, add an event object that contains information about the event, if you can find it.'

  const documentSection = `The document is as follows:\n${transcript}`

  const peopleListSection = `The list of people is as follows:\n${JSON.stringify(parsedData)}`

  const relationshipInstructions =
    'Look through the list of persons for any mention of a person name in the relationship property. ' +
    'If you find a name in the relationship property, add a property called relationshipLinks, which an array ' +
    'of the unique ids of any people whose names you found.'

  const primaryPersonInstructions =
    'If the person has the property "primary: true", meaning they are the principle person in the the document ' +
    'or the person who the event recorded in the document is primarily about, create an object called event. ' +
    "Add this newly created object to an events array in the primary person's object."

  const eventInstructions =
    'Additionally, if you find any mention of an event valuable to genealogists, like a birth date or place ' +
    'or any information about a death or marriage, create an event object. The event objects should be in this ' +
    'format({ eventType: [Text describing the event], date: [The date of the event], place: [The place of the event] }). ' +
    'Add the event objects to the an events array in the object correlating to the person the event happened to, ' +
    'so if the event is a death, put the event object in the person object of the person who died. ' +
    'If this person is the primary person, add this event in addition to the first event you generated.'

  const finalInstruction = 'Return the array with the modifications generated in this step.'

  return `${introduction}\n\n${documentSection}\n\n${peopleListSection}\n\n${relationshipInstructions}\n\n${primaryPersonInstructions}\n\n${eventInstructions}\n\n${finalInstruction}`
}

// Message objects for AI requests
export const createPersonExtractionMessages = (transcript) => [
  GENEALOGIST_SYSTEM_MESSAGE_OBJECT,
  {
    role: 'user',
    content: createPersonExtractionPrompt(transcript),
  },
]

export const createRelationshipLinkingMessages = (transcript, parsedData) => [
  {
    role: 'system',
    content: RELATIONSHIP_ANALYZER_SYSTEM_MESSAGE,
  },
  {
    role: 'user',
    content: createRelationshipLinkingPrompt(transcript, parsedData),
  },
]

export function extractAndParseJson(text) {
  try {
    // Check if the input contains multiple JSON blocks
    const jsonBlocks = text.match(/```json(.*?)```/gs)
    if (!jsonBlocks) {
      throw new Error('No valid JSON found')
    }
    if (jsonBlocks.length > 1) {
      // Parse each JSON block and combine them into an array
      return jsonBlocks.map((block) => {
        const jsonString = block.replace(/```json|```/g, '').trim()
        return JSON.parse(jsonString)
      })
    }
    if (jsonBlocks[0]) {
      return JSON.parse(jsonBlocks[0].replace(/```json|```/g, '').trim())
    }
    throw new Error('No valid JSON found')
  } catch (error) {
    console.error('Error extracting or parsing JSON:', error.message)
    return null
  }
}
